import { useEffect, useRef, useState } from "react";
import { applyPageMetadata } from "../../lib/seo";
import "./tantang-batasanmu.css";

const W = 480;
const H = 270;
const ZONES = ["Rimba Senja", "Gerbang Batu", "Puncak Malam"];
const ASSET_ROOT = "/games/tantang-batasanmu/";
const ART_FILES = [
  "forest.webp", "citadel.webp", "summit.webp",
  "swordmaster-idle-sheet.png", "swordmaster-walk-sheet.png", "swordmaster-attack-sheet.png", "swordmaster-skill-sheet.png", "swordmaster-ultimate-sheet.png",
  "blade-dancer-idle-sheet.png", "blade-dancer-walk-sheet.png", "blade-dancer-attack-sheet.png", "blade-dancer-skill-sheet.png", "blade-dancer-ultimate-sheet.png",
  "ghoul-walk-sheet.png", "ghoul-skill-sheet.png", "guardian-walk-sheet.png", "guardian-skill-sheet.png", "guardian-ultimate-sheet.png",
  "vfx-swordmaster-attack-sheet.png", "vfx-swordmaster-skill-sheet.png", "vfx-swordmaster-ultimate-sheet.png",
  "vfx-blade-dancer-attack-sheet.png", "vfx-blade-dancer-skill-sheet.png", "vfx-blade-dancer-ultimate-sheet.png",
  "vfx-ghoul-skill-sheet.png", "vfx-guardian-skill-sheet.png", "vfx-guardian-ultimate-sheet.png",
  "vfx-impact-amber-sheet.png", "vfx-impact-cyan-sheet.png", "vfx-impact-red-sheet.png", "vfx-defeat-green-sheet.png", "vfx-dash-cyan-sheet.png",
] as const;
type ArtFile = typeof ART_FILES[number];
type Art = Partial<Record<ArtFile, HTMLImageElement>>;
type Role = "swordmaster" | "blade dancer";
type Action = "attack" | "skill" | "ultimate";
type EnemyAction = "skill" | "ultimate";
type Enemy = { x: number; y: number; hp: number; max: number; speed: number; hurt: number; attack: number; kind: number; action: EnemyAction | null; actionClock: number; hitPending: boolean; casts: number; walkClock: number };
type Fx = { x: number; y: number; clock: number; duration: number; size: number; file: ArtFile; frames: number; rotation: number };
type Game = {
  role: Role; x: number; y: number; hp: number; maxHp: number; level: number; xp: number;
  zone: number; wave: number; enemies: Enemy[]; effects: Fx[]; kills: number;
  phase: "fight" | "clear" | "dead" | "won"; tick: number; slashAngle: number;
  moving: number; walkCycle: number; action: Action | null; actionClock: number; nextHit: number;
  attackCd: number; skillCd: number; dashCd: number; invuln: number; facing: number; ultimate: number;
  message: string; messageTime: number; shake: number;
};
type Hud = { hp: number; maxHp: number; level: number; xp: number; zone: number; wave: number; enemies: number; kills: number; phase: Game["phase"]; skill: number; dash: number; ultimate: number; message: string };
const hudOf = (g: Game): Hud => ({ hp: g.hp, maxHp: g.maxHp, level: g.level, xp: g.xp, zone: g.zone, wave: g.wave, enemies: g.enemies.length, kills: g.kills, phase: g.phase, skill: g.skillCd, dash: g.dashCd, ultimate: g.ultimate, message: g.messageTime > 0 ? g.message : "" });
const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));
const rand = (a: number, b: number) => a + Math.random() * (b - a);
const distance = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y);

function spawn(g: Game) {
  const count = 3 + g.wave * 2 + g.zone * 2;
  g.enemies = Array.from({ length: count }, (_, i) => {
    const edge = i % 4;
    const kind = g.wave === 3 && i === count - 1 ? 2 : i % 4 === 0 ? 1 : 0;
    const hp = (kind === 2 ? 100 : kind === 1 ? 48 : 32) + g.zone * 14 + g.wave * 6;
    return { x: edge === 0 ? rand(25, 455) : edge === 1 ? 15 : edge === 2 ? 465 : rand(25, 455),
      y: edge === 0 ? 49 : edge === 3 ? 247 : rand(65, 239), hp, max: hp,
      speed: (kind === 1 ? 29 : kind === 2 ? 17 : 23) + g.zone * 2, hurt: 0, attack: rand(.2, .8), kind,
      action: null, actionClock: 0, hitPending: false, casts: 0, walkClock: rand(0, 1) };
  });
  g.phase = "fight";
  g.message = `WAVE ${g.wave}  •  ${ZONES[g.zone]}`;
  g.messageTime = 2.2;
}
function newGame(role: Role): Game {
  const g: Game = { role, x: 240, y: 175, hp: role === "swordmaster" ? 140 : 105,
    maxHp: role === "swordmaster" ? 140 : 105, level: 1, xp: 0, zone: 0, wave: 1,
    enemies: [], effects: [], kills: 0, phase: "fight", tick: 0, slashAngle: 0,
    moving: 0, walkCycle: 0, action: null, actionClock: 0, nextHit: 0,
    attackCd: 0, skillCd: 0, dashCd: 0, invuln: 0, facing: 0, ultimate: 0, message: "", messageTime: 0, shake: 0 };
  spawn(g);
  return g;
}
function spawnFx(g: Game, file: ArtFile, x: number, y: number, size: number, frames = 8, rotation = 0) {
  g.effects.push({ x, y, size, file, frames, rotation, clock: 0, duration: frames / 12 });
}
function hitEnemy(g: Game, e: Enemy, damage: number) {
  if (e.hurt > 0) return;
  e.hp -= damage;
  e.hurt = .17;
  const d = Math.max(1, distance(g, e));
  e.x += (e.x - g.x) / d * 12;
  e.y += (e.y - g.y) / d * 12;
  if (e.hp <= 0) {
    g.kills++;
    g.xp += e.kind === 2 ? 28 : e.kind === 1 ? 14 : 9;
    g.ultimate = Math.min(100, g.ultimate + (e.kind === 2 ? 45 : e.kind === 1 ? 22 : 14));
    spawnFx(g, "vfx-defeat-green-sheet.png", e.x, e.y - 20, e.kind === 2 ? 92 : 58);
    if (g.xp >= g.level * 45) {
      g.xp -= g.level * 45; g.level++; g.maxHp += 12;
      g.hp = Math.min(g.maxHp, g.hp + 40);
      g.message = `LEVEL ${g.level}  •  HP +40`; g.messageTime = 1.7;
    }
  } else if (g.action === "attack") {
    spawnFx(g, g.role === "blade dancer" ? "vfx-impact-cyan-sheet.png" : "vfx-impact-amber-sheet.png", e.x, e.y - 16, e.kind === 2 ? 54 : 38);
  }
}
function hitMarks(g: Game) {
  if (g.action === "attack") return g.role === "blade dancer" ? [.27, .51, .73] : [.31, .66];
  if (g.action === "skill") return [.62];
  return [.72];
}
function resolveStrike(g: Game, action: Action, hitIndex: number) {
  const dual = g.role === "blade dancer";
  const reach = action === "ultimate" ? 138 : action === "skill" ? (dual ? 82 : 96) : (dual ? 58 : 70);
  const base = action === "ultimate" ? (dual ? 118 : 150) : action === "skill" ? (dual ? 56 : 72) : (dual ? 15 : 23);
  const damage = base + (g.level - 1) * (action === "attack" ? 3 : 7);
  g.shake = action === "ultimate" ? .35 : action === "skill" ? .2 : .08;
  for (const e of g.enemies) {
    const angle = Math.atan2(e.y - g.y, e.x - g.x);
    const diff = Math.atan2(Math.sin(angle - g.slashAngle), Math.cos(angle - g.slashAngle));
    const allAround = action !== "attack";
    if (distance(g, e) < reach && (allAround || Math.abs(diff) < (dual ? 1.5 : 1.18))) hitEnemy(g, e, damage);
  }
  void hitIndex;
}
function startAction(g: Game, action: Action) {
  if (g.phase !== "fight" || g.action || (action === "skill" && g.skillCd > 0) || (action === "attack" && g.attackCd > 0) || (action === "ultimate" && g.ultimate < 100)) return;
  const dual = g.role === "blade dancer";
  g.action = action;
  g.actionClock = 0;
  g.nextHit = 0;
  g.slashAngle = g.facing;
  if (action === "attack") g.attackCd = 1;
  if (action === "skill") { g.skillCd = dual ? 5 : 6; g.message = dual ? "BLADE STORM!" : "EARTHBREAKER!"; g.messageTime = .9; }
  if (action === "ultimate") { g.ultimate = 0; g.message = dual ? "ZERO HORIZON!" : "SOLAR LIMIT!"; g.messageTime = 1.2; }
}
function damagePlayer(g: Game, damage: number) {
  if (g.invuln > 0) return;
  g.hp = Math.max(0, g.hp - damage);
  g.invuln = .48; g.shake = .15;
  spawnFx(g, "vfx-impact-red-sheet.png", g.x, g.y - 20, 58);
  if (g.hp === 0) { g.phase = "dead"; g.message = "PERJALANAN BERAKHIR"; g.messageTime = 10; }
}
function update(g: Game, dt: number, keys: Set<string>, stick: { x: number; y: number }, commands: { attack: boolean; skill: boolean; ultimate: boolean; dash: boolean }) {
  g.tick += dt;
  for (const key of ["attackCd", "skillCd", "dashCd", "invuln", "messageTime", "shake"] as const) g[key] = Math.max(0, g[key] - dt);
  for (const effect of g.effects) effect.clock += dt;
  g.effects = g.effects.filter(effect => effect.clock < effect.duration);
  if (g.phase === "dead" || g.phase === "won") return;
  let mx = (keys.has("d") || keys.has("arrowright") ? 1 : 0) - (keys.has("a") || keys.has("arrowleft") ? 1 : 0) + stick.x;
  let my = (keys.has("s") || keys.has("arrowdown") ? 1 : 0) - (keys.has("w") || keys.has("arrowup") ? 1 : 0) + stick.y;
  const len = Math.hypot(mx, my);
  if (len > 0) { mx /= len; my /= len; g.facing = Math.atan2(my, mx); }
  g.moving += ((len > 0 ? 1 : 0) - g.moving) * Math.min(1, dt * 12);
  if (len > 0 && !g.action) {
    g.walkCycle += dt * 8;
  }
  if (commands.dash && g.dashCd <= 0 && len > 0 && !g.action) {
    g.dashCd = 2.7; g.invuln = .32;
    const startX = g.x, startY = g.y;
    g.x = clamp(g.x + mx * 52, 18, 462); g.y = clamp(g.y + my * 52, 66, 249);
    spawnFx(g, "vfx-dash-cyan-sheet.png", (startX + g.x) / 2, (startY + g.y) / 2 - 13, 76, 8, g.facing);
  }
  const moveScale = g.action ? .12 : 1;
  g.x = clamp(g.x + mx * (g.role === "blade dancer" ? 105 : 84) * dt * moveScale, 18, 462);
  g.y = clamp(g.y + my * (g.role === "blade dancer" ? 105 : 84) * dt * moveScale, 62, 249);
  if (g.phase === "clear") {
    if (g.y < 75 && Math.abs(g.x - 240) < 48) {
      g.zone++; g.wave = 1; g.x = 240; g.y = 222;
      g.hp = Math.min(g.maxHp, g.hp + 48); spawn(g);
    }
    return;
  }
  if (commands.ultimate) startAction(g, "ultimate");
  else if (commands.skill) startAction(g, "skill");
  else if (commands.attack) startAction(g, "attack");
  if (g.action) {
    g.actionClock += dt;
    const marks = hitMarks(g);
    while (g.nextHit < marks.length && g.actionClock >= marks[g.nextHit]) {
      resolveStrike(g, g.action, g.nextHit); g.nextHit++;
    }
    if (g.actionClock >= 1) { g.action = null; g.actionClock = 0; }
  }
  for (const e of g.enemies) {
    e.hurt = Math.max(0, e.hurt - dt); e.attack = Math.max(0, e.attack - dt);
    const d = Math.max(1, distance(g, e));
    if (e.action) {
      e.actionClock += dt;
      const delay = e.action === "ultimate" ? .08 : .1;
      const impact = delay + (1 - delay) * (e.action === "ultimate" ? 8 / 12 : 7 / 12);
      if (e.hitPending && e.actionClock >= impact) {
        e.hitPending = false;
        const radius = e.action === "ultimate" ? 68 : e.kind === 2 ? 37 : 25;
        if (distance(g, e) <= radius) damagePlayer(g, e.action === "ultimate" ? 31 : e.kind === 2 ? 21 : e.kind === 1 ? 14 : 9);
      }
      if (e.actionClock >= 1) { e.action = null; e.actionClock = 0; e.attack = e.kind === 2 ? 1.2 : .7; }
    } else if (d > (e.kind === 2 ? 34 : 21)) {
      e.x += (g.x - e.x) / d * e.speed * dt; e.y += (g.y - e.y) / d * e.speed * dt; e.walkClock += dt * 8;
    } else if (e.attack <= 0) {
      e.action = e.kind === 2 && e.casts % 3 === 2 ? "ultimate" : "skill";
      e.actionClock = 0; e.hitPending = true; e.casts++;
    }
  }
  g.enemies = g.enemies.filter(e => e.hp > 0);
  if (!g.enemies.length && g.phase === "fight") {
    if (g.wave < 3) { g.wave++; g.hp = Math.min(g.maxHp, g.hp + 15); spawn(g); }
    else if (g.zone === ZONES.length - 1) { g.phase = "won"; g.message = "SEMUA BATAS TERLAMPAUI!"; g.messageTime = 10; }
    else { g.phase = "clear"; g.message = "AREA BERSIH!  JALAN KE UTARA ↑"; g.messageTime = 4; }
  }
}

function rect(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string) {
  c.fillStyle = color; c.fillRect(Math.round(x), Math.round(y), w, h);
}
function drawFighter(c: CanvasRenderingContext2D, x: number, y: number, role: Role, enemy = false, kind = 0, flash = false) {
  c.save(); c.translate(Math.round(x), Math.round(y));
  const glow = flash ? "#fff8da" : enemy ? kind === 2 ? "#f45b79" : "#a2d276" : role === "swordmaster" ? "#ffd276" : "#86f4ee";
  rect(c, -9, 1, 18, 3, "#0d1720a0");
  c.fillStyle = "#292d3a";
  for (const s of [-1, 1]) {
    c.beginPath(); c.moveTo(s * 3, -9); c.lineTo(s * 10, -2); c.lineTo(s * 5, 1); c.fill();
    c.beginPath(); c.moveTo(s * 8, -24); c.lineTo(s * 13, -14); c.lineTo(s * 8, -11); c.fill();
  }
  c.beginPath(); c.moveTo(0, -34); c.lineTo(9, -27); c.lineTo(6, -12); c.lineTo(0, -8); c.lineTo(-6, -12); c.lineTo(-9, -27); c.closePath(); c.fill();
  c.fillStyle = glow;
  c.beginPath(); c.moveTo(0, -24); c.lineTo(5, -18); c.lineTo(0, -12); c.lineTo(-5, -18); c.closePath(); c.fill();
  rect(c, -3, -31, 6, 2, glow);
  if (!enemy) { rect(c, role === "swordmaster" ? -15 : 12, -24, 3, 20, glow); if (role === "blade dancer") rect(c, -15, -24, 3, 20, glow); }
  c.restore();
}
function drawSprite(c: CanvasRenderingContext2D, image: HTMLImageElement, x: number, y: number, frame: number, size: number, flip = false, lean = 0) {
  c.save();
  c.fillStyle = "#0d1720a0"; c.beginPath(); c.ellipse(Math.round(x), Math.round(y), size * .33, 4, 0, 0, Math.PI * 2); c.fill();
  c.translate(Math.round(x), Math.round(y - size / 2));
  c.rotate(lean);
  if (flip) c.scale(-1, 1);
  const cell = image.naturalHeight;
  c.drawImage(image, frame * cell, 0, cell, cell, Math.round(-size / 2), Math.round(-size / 2), size, size);
  c.restore();
}
function drawEffectSprite(c: CanvasRenderingContext2D, image: HTMLImageElement, x: number, y: number, frame: number, size: number, rotation = 0) {
  c.save();
  c.translate(Math.round(x), Math.round(y));
  c.rotate(rotation);
  const cell = image.naturalHeight;
  c.drawImage(image, frame * cell, 0, cell, cell, Math.round(-size / 2), Math.round(-size / 2), size, size);
  c.restore();
}
function drawEffectLayer(c: CanvasRenderingContext2D, image: HTMLImageElement, x: number, y: number, frame: number, size: number, layer: "back" | "front", rotation = 0) {
  c.save();
  c.translate(Math.round(x), Math.round(y));
  c.rotate(rotation);
  const cell = image.naturalHeight;
  const split = Math.floor(cell * .53);
  const sourceY = layer === "back" ? 0 : split;
  const sourceHeight = layer === "back" ? split : cell - split;
  const scale = size / cell;
  c.drawImage(
    image,
    frame * cell,
    sourceY,
    cell,
    sourceHeight,
    Math.round(-size / 2),
    Math.round(-size / 2 + sourceY * scale),
    size,
    sourceHeight * scale,
  );
  c.restore();
}
function actionEffectFrame(clock: number, delay: number) {
  if (clock < delay) return -1;
  return Math.min(11, Math.floor(((clock - delay) / (1 - delay)) * 12));
}
function playerEffectPlacement(role: Role, action: Action) {
  if (action === "skill") return role === "swordmaster"
    ? { size: 98, y: -3, delay: .1, rotate: false }
    : { size: 94, y: -8, delay: .1, rotate: false };
  if (action === "ultimate") return role === "swordmaster"
    ? { size: 136, y: -18, delay: .04, rotate: false }
    : { size: 128, y: -16, delay: .06, rotate: false };
  return { size: 78, y: -23, delay: .05, rotate: true };
}
function draw(c: CanvasRenderingContext2D, g: Game, art: Art) {
  c.save(); c.imageSmoothingEnabled = false;
  c.clearRect(0, 0, W, H);
  if (g.shake) c.translate(Math.round(rand(-2, 2)), Math.round(rand(-2, 2)));
  const palettes = [
    ["#172e32", "#294a45", "#3d6252", "#82a47b"],
    ["#252a39", "#414757", "#5a6071", "#aaa08c"],
    ["#252536", "#403449", "#645066", "#aa8494"],
  ];
  const [dark, ground, tile, accent] = palettes[g.zone];
  const bg = art[(["forest.webp", "citadel.webp", "summit.webp"] as const)[g.zone]];
  rect(c, 0, 0, W, H, dark);
  if (bg?.complete && bg.naturalWidth) c.drawImage(bg, 0, 48, W, 222);
  else {
    rect(c, 0, 48, W, 222, ground);
    for (let row = 0; row < 14; row++) for (let col = 0; col < 25; col++) {
      const n = (col * 17 + row * 31 + g.zone * 13) % 11;
      if (n < 4) rect(c, col * 20 + n * 2, 50 + row * 16 + (n % 3), n === 0 ? 8 : 4, 2, tile);
    }
    rect(c, 211, 48, 58, 222, tile);
    for (let i = 0; i < 13; i++) rect(c, 214 + i % 2 * 3, 59 + i * 17, 49, 1, ground);
  }
  if (g.phase === "clear") {
    rect(c, 218, 53, 44, 23, "#82e7c1"); rect(c, 222, 54, 36, 17, "#27645e");
    c.fillStyle = "#f7e9af"; c.font = "bold 12px monospace"; c.textAlign = "center"; c.fillText("↑", 240, 68);
  }
  const actors = [...g.enemies.map(e => ({ y: e.y, e })), { y: g.y, e: null }].sort((a, b) => a.y - b.y);
  for (const actor of actors) {
    if (actor.e) {
      const e = actor.e;
      const family = e.kind === 2 ? "guardian" : "ghoul";
      const state = e.action ?? "walk";
      const sprite = art[`${family}-${state}-sheet.png` as ArtFile];
      const frame = e.action ? Math.min(11, Math.floor(e.actionClock * 12)) : Math.floor(e.walkClock) % 8;
      const lean = e.action ? Math.sin(e.actionClock * Math.PI) * .045 : Math.sin(e.walkClock * Math.PI / 4) * .025;
      let actionEffect: HTMLImageElement | undefined;
      let actionFxFrame = -1;
      let actionEffectSize = 0;
      let actionEffectY = e.y;
      if (e.action) {
        actionEffect = art[`vfx-${family}-${e.action}-sheet.png` as ArtFile];
        const delay = e.action === "ultimate" ? .08 : .1;
        actionFxFrame = actionEffectFrame(e.actionClock, delay);
        actionEffectSize = e.action === "ultimate" ? 118 : e.kind === 2 ? 82 : 58;
        actionEffectY = e.y - (e.kind === 2 ? 5 : 2);
        if (actionEffect?.complete && actionEffect.naturalWidth && actionFxFrame >= 0) drawEffectLayer(c, actionEffect, e.x, actionEffectY, actionFxFrame, actionEffectSize, "back");
      }
      if (sprite?.complete && sprite.naturalWidth) drawSprite(c, sprite, e.x, e.y, frame, e.kind === 2 ? 64 : e.kind === 1 ? 42 : 36, e.x > g.x, lean);
      else drawFighter(c, e.x, e.y, g.role, true, e.kind, e.hurt > 0);
      if (actionEffect?.complete && actionEffect.naturalWidth && actionFxFrame >= 0) drawEffectLayer(c, actionEffect, e.x, actionEffectY, actionFxFrame, actionEffectSize, "front");
      if (e.hp < e.max) { rect(c, e.x - 10, e.y - 34, 20, 3, dark); rect(c, e.x - 10, e.y - 34, 20 * e.hp / e.max, 3, "#e57471"); }
    } else if (g.invuln <= 0 || Math.floor(g.tick * 16) % 2 === 0) {
      const prefix = g.role === "swordmaster" ? "swordmaster" : "blade-dancer";
      const attacking = Boolean(g.action);
      const state = g.action ?? (g.moving > .25 ? "walk" : "idle");
      const sprite = art[`${prefix}-${state}-sheet.png` as ArtFile];
      const progress = attacking ? clamp(g.actionClock, 0, .999) : 0;
      const frame = attacking ? Math.min(11, Math.floor(progress * 12)) : state === "walk" ? Math.floor(g.walkCycle) % 8 : Math.floor(g.tick * 5) % 4;
      const flip = Math.cos(g.facing) < -.2;
      const lean = attacking ? (progress < .3 ? -.055 : progress < .82 ? .08 : 0) : Math.sin(g.walkCycle * Math.PI / 4) * g.moving * .035;
      const lunge = attacking && progress > .3 && progress < .84 ? Math.sin((progress - .3) / .54 * Math.PI) * (g.role === "blade dancer" ? 9 : 6) : 0;
      const x = g.x + Math.cos(g.slashAngle) * lunge;
      const y = g.y + Math.sin(g.slashAngle) * lunge;
      const size = g.role === "swordmaster" ? 48 : 46;
      let actionEffect: HTMLImageElement | undefined;
      let actionFxFrame = -1;
      let actionFxSize = 0;
      let actionFxY = y;
      let actionFxRotation = 0;
      if (g.action) {
        const placement = playerEffectPlacement(g.role, g.action);
        actionEffect = art[`vfx-${prefix}-${g.action}-sheet.png` as ArtFile];
        actionFxFrame = actionEffectFrame(g.actionClock, placement.delay);
        actionFxSize = placement.size;
        actionFxY = y + placement.y;
        actionFxRotation = placement.rotate ? g.slashAngle : 0;
        if (actionEffect?.complete && actionEffect.naturalWidth && actionFxFrame >= 0) drawEffectLayer(c, actionEffect, x, actionFxY, actionFxFrame, actionFxSize, "back", actionFxRotation);
      }
      if (sprite?.complete && sprite.naturalWidth) {
        drawSprite(c, sprite, x, y, frame, size, flip, lean);
      } else drawFighter(c, g.x, g.y, g.role);
      if (actionEffect?.complete && actionEffect.naturalWidth && actionFxFrame >= 0) drawEffectLayer(c, actionEffect, x, actionFxY, actionFxFrame, actionFxSize, "front", actionFxRotation);
    }
  }
  for (const effect of g.effects) {
    const image = art[effect.file];
    if (image?.complete && image.naturalWidth) drawEffectSprite(c, image, effect.x, effect.y, Math.min(effect.frames - 1, Math.floor(effect.clock * 12)), effect.size, effect.rotation);
  }
  rect(c, 0, 0, W, 48, "#131d2c"); rect(c, 0, 45, W, 3, accent);
  c.fillStyle = "#f4dfad"; c.textAlign = "center"; c.font = "bold 11px monospace";
  c.fillText(`${ZONES[g.zone].toUpperCase()}  /  WAVE ${g.wave} · 3`, 240, 18);
  c.fillStyle = "#9cb6ad"; c.font = "9px monospace";
  c.fillText(g.phase === "clear" ? "AREA BERSIH  •  MASUK GERBANG UTARA ↑" : `MUSUH TERSISA ${g.enemies.length}   •   KALAHKAN SEMUANYA`, 240, 34);
  c.restore();
}

export function TantangBatasanmuPage() {
  const canvas = useRef<HTMLCanvasElement>(null);
  const art = useRef<Art>({});
  const game = useRef<Game | null>(null);
  const keys = useRef(new Set<string>());
  const stick = useRef({ x: 0, y: 0 });
  const commands = useRef({ attack: false, skill: false, ultimate: false, dash: false });
  const [hud, setHud] = useState<Hud | null>(null);
  const [selected, setSelected] = useState<Role>("swordmaster");
  const [stickVisual, setStickVisual] = useState({ x: 0, y: 0 });
  const joystick = useRef<HTMLDivElement>(null);
  const start = (role: Role) => { game.current = newGame(role); setHud(hudOf(game.current)); };
  useEffect(() => {
    applyPageMetadata({ title: "Tantang Batasanmu | Steria House", description: "Pixel survival RPG: Swordmaster atau Blade Dancer, lawan wave musuh dan jelajahi tiga area.", canonicalPath: "/games/tantang-batasanmu" });
    for (const file of ART_FILES) { const image = new Image(); image.src = ASSET_ROOT + file; art.current[file] = image; }
    const down = (e: KeyboardEvent) => { const k = e.key.toLowerCase(); if (["arrowup", "arrowdown", "arrowleft", "arrowright", " "].includes(k)) e.preventDefault(); keys.current.add(k); };
    const up = (e: KeyboardEvent) => keys.current.delete(e.key.toLowerCase());
    const blur = () => { keys.current.clear(); stick.current = { x: 0, y: 0 }; commands.current = { attack: false, skill: false, ultimate: false, dash: false }; };
    window.addEventListener("keydown", down); window.addEventListener("keyup", up); window.addEventListener("blur", blur);
    let frame = 0, last = performance.now(), uiTime = 0;
    const loop = (now: number) => {
      const dt = Math.min((now - last) / 1000, .04); last = now;
      const g = game.current;
      if (g) {
        const k = keys.current;
        update(g, dt, k, stick.current, { attack: commands.current.attack || k.has("j") || k.has(" "), skill: commands.current.skill || k.has("k"), ultimate: commands.current.ultimate || k.has("l"), dash: commands.current.dash || k.has("shift") });
        commands.current.dash = false;
        commands.current.ultimate = false;
        const ctx = canvas.current?.getContext("2d"); if (ctx) draw(ctx, g, art.current);
        uiTime += dt; if (uiTime > .1 || g.phase !== "fight") { setHud(hudOf(g)); uiTime = 0; }
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(frame); window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); window.removeEventListener("blur", blur); };
  }, []);
  const moveStick = (e: React.PointerEvent<HTMLDivElement>) => {
    const box = joystick.current?.getBoundingClientRect(); if (!box) return;
    const x = clamp((e.clientX - box.left - box.width / 2) / (box.width * .33), -1, 1);
    const y = clamp((e.clientY - box.top - box.height / 2) / (box.height * .33), -1, 1);
    stick.current = { x, y }; setStickVisual({ x, y });
  };
  const stopStick = () => { stick.current = { x: 0, y: 0 }; setStickVisual({ x: 0, y: 0 }); };
  return <main className="tb-game">
    <div className="tb-frame">
      <div className="tb-top"><span className="tb-brand">✦ STERIA HOUSE <b>/ TANTANG BATASANMU</b></span><span className="tb-top-right">PIXEL SURVIVAL RPG <i>●</i> LOCAL PLAY</span></div>
      <div className="tb-stage">
        <canvas ref={canvas} width={W} height={H} aria-label="Arena pixel survival" />
        {hud && <div className="tb-status"><div className="tb-health"><span>♥</span><div><strong>HP {hud.hp} / {hud.maxHp}</strong><div className="tb-meter"><i style={{ width: `${hud.hp / hud.maxHp * 100}%` }} /></div></div></div><div className="tb-level">LV {hud.level}<div className="tb-xp"><i style={{ width: `${hud.xp / (hud.level * 45) * 100}%` }} /></div></div><div className={`tb-ultimate-meter ${hud.ultimate >= 100 ? "ready" : ""}`}>ULT {Math.floor(hud.ultimate)}%<div><i style={{ width: `${hud.ultimate}%` }} /></div></div></div>}
        {hud?.message && hud.phase === "fight" && <div className="tb-announcement">{hud.message}</div>}
        {hud?.phase === "clear" && <div className="tb-clear"><span>AREA BERSIH</span><small>Jalan ke gerbang utara untuk lanjut ↑</small></div>}
        {(!hud || hud.phase === "dead" || hud.phase === "won") && <div className="tb-menu">
          <div className="tb-menu-card">
            <span className="tb-eyebrow">✦ A PIXEL SURVIVAL ADVENTURE ✦</span>
            <h1>{hud?.phase === "dead" ? "COBA LAGI" : hud?.phase === "won" ? "BATAS TERLAMPAUI" : <>TANTANG<br /><em>BATASANMU</em></>}</h1>
            <p>{hud?.phase === "dead" ? `Kamu menumbangkan ${hud.kills} musuh. Bangkit dan coba lagi.` : hud?.phase === "won" ? `Tiga area ditaklukkan. ${hud.kills} musuh tumbang!` : "Pilih jalanmu. Tahan gelombang musuh, lalu terus bergerak ke utara."}</p>
            <div className="tb-roles">
              <button className={selected === "swordmaster" ? "active" : ""} onClick={() => setSelected("swordmaster")}><img className="tb-role-image" src={ASSET_ROOT + "swordmaster-idle.gif"} alt="" /><strong>SWORDMASTER</strong><small>Earthbreaker · Solar Limit<br />Pedang berat · HP tinggi</small></button>
              <button className={selected === "blade dancer" ? "active" : ""} onClick={() => setSelected("blade dancer")}><img className="tb-role-image" src={ASSET_ROOT + "blade-dancer-idle.gif"} alt="" /><strong>BLADE DANCER</strong><small>Blade Storm · Zero Horizon<br />Dua bilah · Gerak cepat</small></button>
            </div>
            <button className="tb-start" onClick={() => start(selected)}>{hud ? "MAIN LAGI" : "MULAI PETUALANGAN"} <span>→</span></button>
            <small className="tb-help">WASD / PANAH gerak · J serang · K skill · L ultimate · SHIFT dash</small>
          </div>
        </div>}
        {hud && hud.phase !== "dead" && hud.phase !== "won" && <div className="tb-controls">
          <div className="tb-stick" ref={joystick} onPointerDown={e => { e.currentTarget.setPointerCapture(e.pointerId); moveStick(e); }} onPointerMove={e => { if (e.currentTarget.hasPointerCapture(e.pointerId)) moveStick(e); }} onPointerUp={stopStick} onPointerCancel={stopStick}><div className="tb-knob" style={{ transform: `translate(${stickVisual.x * 28}px, ${stickVisual.y * 28}px)` }} /></div>
          <div className="tb-actions"><button className="tb-dash" onPointerDown={e => { e.preventDefault(); commands.current.dash = true; }} disabled={hud.dash > 0}>DASH<small>{hud.dash > 0 ? Math.ceil(hud.dash) : "↗"}</small></button><button className="tb-ultimate" onPointerDown={e => { e.preventDefault(); if (game.current) startAction(game.current, "ultimate"); }} disabled={hud.ultimate < 100}>ULT<small>{hud.ultimate >= 100 ? "◆" : Math.floor(hud.ultimate)}</small></button><button className="tb-skill" onPointerDown={e => { e.preventDefault(); if (game.current) startAction(game.current, "skill"); }} disabled={hud.skill > 0}>SKILL<small>{hud.skill > 0 ? Math.ceil(hud.skill) : "✦"}</small></button><button className="tb-attack" onPointerDown={e => { e.preventDefault(); if (game.current) startAction(game.current, "attack"); commands.current.attack = true; e.currentTarget.setPointerCapture(e.pointerId); }} onPointerUp={() => { commands.current.attack = false; }} onPointerCancel={() => { commands.current.attack = false; }}>SERANG<small>⚔</small></button></div>
        </div>}
      </div>
      <div className="tb-bottom"><span>GERAK <b>W A S D</b> / <b>↕ ↔</b></span><span>SERANG <b>J</b> · SKILL <b>K</b> · ULT <b>L</b> · DASH <b>SHIFT</b></span><span>3 AREA · 9 WAVE · 2 ROLE</span></div>
    </div>
    <div className="tb-rotate">↻<strong>Putar HP-mu</strong><span>Game ini dimainkan dalam mode landscape.</span></div>
  </main>;
}
